import { Dashboard } from "@/components/dashboard/Dashboard/Dashboard";
import { VStack } from "@/components/layout/VStack";
import { useRotations } from "../hooks/rotations/useRotations";
import { useEffect, useState } from "react";
import { RotationsTable } from "../components/rotations/RotationsDataTable";
import { HStack } from "@/components/layout/HStack";
import { Button } from "@/components/ui/Button/Button";
import { CalendarSync, LucideCalendarPlus } from "lucide-react";
import { Skeleton } from "@/components/ui/Skeleton/Skeleton";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/Empty/Empty";
import { CreateRotationSheet } from "../components/rotations/CreateRotationSheet";
import { EditRotationSheet } from "../components/rotations/EditRotationSheet";
import { DeleteRotationDialog } from "../components/rotations/DeleteRotationDialog";
import { AssignPreferencesDialog } from "../components/rotations/AssignPreferencesDialog";
import { ArchiveRotationDialog } from "../components/rotations/ArchiveRotationDialog";
import { UnarchiveRotationDialog } from "../components/rotations/UnarchiveRotationDialog";
import { ArchiveToggleButton } from "@/components/elements/ArchiveToggleButton/ArchiveToggleButton";

const AdminRotations = ({
  archived = false,
  sysAdmin = false,
}: {
  archived?: boolean;
  sysAdmin?: boolean;
}) => {
  const [rotations, setRotations] = useState<{
    rotations: any[];
    sessions: any[];
  }>({ rotations: [], sessions: [] });
  const [open, setOpen] = useState<{
    create: boolean;
    edit: { open: boolean; rotation: any };
    delete: { open: boolean; rotation: any };
    assign: { open: boolean; rotation: any };
    archive: { open: boolean; rotation: any };
    unarchive: { open: boolean; rotation: any };
  }>({
    create: false,
    edit: { open: false, rotation: undefined },
    delete: { open: false, rotation: undefined },
    assign: { open: false, rotation: undefined },
    archive: { open: false, rotation: undefined },
    unarchive: { open: false, rotation: undefined },
  });

  const { loadRotations, loading } = useRotations({ archived });

  useEffect(() => {
    loadRotations(setRotations);
  }, [archived]);

  const openEdit = (rotationId: string) => {
    const rotation = rotations.rotations.find((r) => r._id === rotationId);
    if (!rotation) return undefined;

    const sessions = rotations.sessions.filter(
      (s) => s.rotation === rotation._id,
    );

    setOpen((prev) => ({
      ...prev,
      edit: {
        open: true,
        rotation: { ...rotation, sessions },
      },
    }));
  };

  const openDelete = (rotationId: string) => {
    const rotation = rotations.rotations.find((r) => r._id === rotationId);

    setOpen((prev) => ({
      ...prev,
      delete: {
        open: true,
        rotation,
      },
    }));
  };

  const openAssign = (rotationId: string) => {
    const rotation = rotations.rotations.find((r) => r._id === rotationId);
    if (!rotation) return undefined;

    const sessions = rotations.sessions
      .filter((s) => s.rotation === rotation._id)
      .filter((s) => s.singleton === false);

    setOpen((prev) => ({
      ...prev,
      assign: {
        open: true,
        rotation: { ...rotation, sessions },
      },
    }));
  };

  const openArchive = (rotationId: string) => {
    const rotation = rotations.rotations.find((r) => r._id === rotationId);
    if (!rotation) return undefined;

    setOpen((prev) => ({
      ...prev,
      archive: { open: true, rotation },
    }));
  };

  const openUnarchive = (rotationId: string) => {
    const rotation = rotations.rotations.find((r) => r._id === rotationId);
    if (!rotation) return undefined;

    setOpen((prev) => ({
      ...prev,
      unarchive: { open: true, rotation },
    }));
  };

  const refetch = () => loadRotations(setRotations);

  return (
    <Dashboard activePage={archived ? "/rotations/archive" : "/rotations"}>
      <HStack justify="space-between" wrap>
        <VStack>
          <h2
            style={{
              fontSize: "var(--text-2xl)",
              lineHeight: "var(--line-height-2xl)",
              fontWeight: "var(--font-bold)",
            }}
          >
            {archived ? "Archived Rotations" : "Rotations"}
          </h2>
          <p style={{ color: "var(--muted-foreground)" }}>
            {archived
              ? "View archived rotations and sessions."
              : "View, create, and update all rotations and sessions."}
          </p>
        </VStack>
        <HStack>
          <ArchiveToggleButton />
          {!archived && (
            <Button
              onClick={() => setOpen((prev) => ({ ...prev, create: true }))}
            >
              <LucideCalendarPlus /> Create Rotation
            </Button>
          )}
        </HStack>
      </HStack>
      {loading.rotations ? (
        <Skeleton style={{ width: "100%", height: "512px" }} />
      ) : rotations.rotations.length < 1 ? (
        <Empty style={{ borderColor: "var(--secondary-border)" }}>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <CalendarSync />
            </EmptyMedia>
            <EmptyTitle>
              {archived ? "No Archived Rotations" : "No Rotations Found"}
            </EmptyTitle>
            <EmptyDescription>
              {archived
                ? "There are no archived rotations."
                : "There were no rotations found. If you believe this is an error, please refresh."}
            </EmptyDescription>
          </EmptyHeader>
          {!archived && (
            <EmptyContent>
              <Button
                onClick={() => setOpen((prev) => ({ ...prev, create: true }))}
              >
                {" "}
                <LucideCalendarPlus />
                Create Rotation
              </Button>
            </EmptyContent>
          )}
        </Empty>
      ) : (
        <RotationsTable
          data={rotations}
          onEdit={openEdit}
          onDelete={openDelete}
          onAssign={openAssign}
          onArchive={sysAdmin ? openArchive : undefined}
          onUnarchive={sysAdmin ? openUnarchive : undefined}
          archived={archived}
          sysAdmin={sysAdmin}
        />
      )}
      <CreateRotationSheet
        open={open.create}
        onOpenChange={(open) => setOpen((prev) => ({ ...prev, create: open }))}
        setRotations={setRotations}
      />
      {open.edit.rotation && (
        <EditRotationSheet
          open={open.edit.open}
          onOpenChange={(open) =>
            setOpen((prev) => ({
              ...prev,
              edit: { open, rotation: prev.edit.rotation },
            }))
          }
          setRotations={setRotations}
          rotation={open.edit.rotation}
        />
      )}
      {open.delete.rotation && (
        <DeleteRotationDialog
          open={open.delete.open}
          onOpenChange={(open) =>
            setOpen((prev) => ({
              ...prev,
              delete: { open, rotation: prev.delete.rotation },
            }))
          }
          setRotations={setRotations}
          rotation={open.delete.rotation}
        />
      )}
      {open.assign.rotation && (
        <AssignPreferencesDialog
          open={open.assign.open}
          onOpenChange={(open) =>
            setOpen((prev) => ({
              ...prev,
              assign: { open, rotation: prev.assign.rotation },
            }))
          }
          rotation={open.assign.rotation}
        />
      )}
      {open.archive.rotation && (
        <ArchiveRotationDialog
          open={open.archive.open}
          onOpenChange={(open) =>
            setOpen((prev) => ({
              ...prev,
              archive: { open, rotation: prev.archive.rotation },
            }))
          }
          rotation={open.archive.rotation}
          onArchived={() => {
            setOpen((prev) => ({
              ...prev,
              archive: { open: false, rotation: undefined },
            }));
            refetch();
          }}
        />
      )}
      {open.unarchive.rotation && (
        <UnarchiveRotationDialog
          open={open.unarchive.open}
          onOpenChange={(open) =>
            setOpen((prev) => ({
              ...prev,
              unarchive: { open, rotation: prev.unarchive.rotation },
            }))
          }
          rotation={open.unarchive.rotation}
          onUnarchived={() => {
            setOpen((prev) => ({
              ...prev,
              unarchive: { open: false, rotation: undefined },
            }));
            refetch();
          }}
        />
      )}
    </Dashboard>
  );
};

export default AdminRotations;
